import React from 'react';
import { Sprout, FlaskConical, ScrollText, PackageCheck, ShieldCheck } from 'lucide-react';
import PageHero from '../../components/ui-shree/PageHero';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import BottleTwin from '../../components/DigitalTwin/BottleTwin';
import FormulaLedger from '../../components/Transparency/FormulaLedger';
import PurityInfographic from '../../components/Transparency/PurityInfographic';
import InboundPurityLedger from '../../components/Transparency/InboundPurityLedger';
import WaterIsLife from '../../components/Story/WaterIsLife';
import VedicBiophysics from '../../components/Philosophy/VedicBiophysics';

const STEPS = [
  {
    icon: Sprout,
    title: 'Soil & seed',
    location: 'Uttarakhand, India · 30.3245°N',
    date: '2025·09·14',
    hash: '0x91f3…ae07',
    body:
      'Sea-buckthorn berries hand-picked at first light by Cooperative Surya. Soil pH, lot weight, and harvester signature committed on chain at moment of weighing.',
  },
  {
    icon: FlaskConical,
    title: 'Cold-press',
    location: 'Atelier Heavener · 34.8898°N',
    date: '2025·09·22',
    hash: '0x7c9c…b14a',
    body:
      'Berries pressed below 38°C within 8 days of harvest. Press temperature, batch weight, and operator key signed and notarised on chain.',
  },
  {
    icon: ScrollText,
    title: 'Formulation',
    location: 'Atelier Heavener · Room 4',
    date: '2025·10·02',
    hash: '0xaac6…d4e8',
    body:
      'Bakuchiol, niacinamide and hyaluronic acid blended into the base oil. Each ingredient supplier and lot ID is referenced on chain.',
  },
  {
    icon: PackageCheck,
    title: 'Bottling & seal',
    location: 'Hand-numbered, Lot 042',
    date: '2025·10·11',
    hash: '0xc1d7…e2a3',
    body:
      'Filled into recycled glass under nitrogen. Each bottle receives a unique on-chain ID readable through the cap NFC tag.',
  },
  {
    icon: ShieldCheck,
    title: 'Your hand',
    location: 'Anywhere',
    date: 'Now',
    hash: '0x90b0…f2c1',
    body:
      'Tap the cap with your phone. The full provenance opens. The chain confirms what your skin already knows: this is real.',
  },
];

const TransparencyPage = () => (
  <div data-testid="transparency-page">
    <PageHero
      overline="Blockchain Transparency"
      title={<>From soil <em className="text-[#90B0BF]">to skin</em>, signed.</>}
      subtitle="Every Shreesavaa bottle is a small notarised story. Five steps, each one a transaction. Tap, scan, and verify the full provenance — no marketing, just the chain."
      testId="transparency-hero"
    />

    <section className="px-6 md:px-12 pb-24">
      <div className="max-w-4xl mx-auto">
        <SectionLabel testId="trace-label">The Five-Step Provenance</SectionLabel>

        <ol className="relative pl-8 md:pl-12 border-l border-[#AAC6D4]">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              data-testid={`trace-step-${i}`}
              className="relative pb-16 last:pb-0"
            >
              <span
                className="absolute -left-[37px] md:-left-[49px] top-2 w-3 h-3 rounded-full bg-[#7C9CB1] ring-4 ring-[#F4F8F9]"
                aria-hidden
              />
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
                <div className="md:col-span-2">
                  <s.icon size={28} strokeWidth={1.2} className="text-[#7C9CB1]" />
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mt-3">
                    Step {String(i + 1).padStart(2, '0')}
                  </p>
                </div>
                <div className="md:col-span-10">
                  <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
                    {s.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-[#90B0BF]">
                    <span>{s.location}</span>
                    <span>{s.date}</span>
                    <span className="text-[#7C9CB1]">{s.hash}</span>
                  </div>
                  <p className="mt-5 text-[#7C9CB1]/85 font-light leading-relaxed max-w-xl">
                    {s.body}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* Data-Backed Purity infographic — educational bridge for skeptics */}
    <PurityInfographic />

    {/* Vedic Biophysics — the named scientific pillar of the house */}
    <VedicBiophysics
      testId="transparency-vedic-biophysics"
      variant="dark"
      overlineHi="Threshold · The Scientific Pillar · Vedic Biophysics"
    />

    {/* Formula Ledger — canonical Kewra-base composition */}
    <FormulaLedger />

    {/* Inbound Purity Ledger — per-batch drum tamper-seal audit */}
    <InboundPurityLedger />

    {/* The Sanatana Economy — water-is-life manifesto */}
    <WaterIsLife variant="full" testId="transparency-water-is-life" />

    {/* Verification card */}
    <section className="px-6 md:px-12 py-24 bg-[#C1D7E2]/40 border-t border-[#C1D7E2]">
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <SectionLabel>Verify a bottle</SectionLabel>
          <h3 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-tight">
            Hold your phone to the cap.
          </h3>
          <p className="mt-6 text-[#7C9CB1]/85 font-light leading-relaxed">
            Each Shreesavaa bottle has a tamper-evident NFC tag embedded in the
            cap. A single tap opens the on-chain provenance ledger — readable in
            any browser, signed by every hand that touched the formula.
          </p>
        </div>
        <div
          data-testid="verify-mock"
          className="bg-[#F4F8F9] border border-[#C1D7E2] p-6 font-mono text-[12px] text-[#7C9CB1] space-y-3"
        >
          <div className="flex justify-between border-b border-[#C1D7E2] pb-2">
            <span className="text-[#90B0BF] tracking-[0.2em] uppercase">Bottle</span>
            <span>SH-042-0188</span>
          </div>
          <div className="flex justify-between border-b border-[#C1D7E2] pb-2">
            <span className="text-[#90B0BF] tracking-[0.2em] uppercase">Block</span>
            <span>#21,884,132</span>
          </div>
          <div className="flex justify-between border-b border-[#C1D7E2] pb-2">
            <span className="text-[#90B0BF] tracking-[0.2em] uppercase">Status</span>
            <span>Verified</span>
          </div>
          <div className="flex justify-between border-b border-[#C1D7E2] pb-2">
            <span className="text-[#90B0BF] tracking-[0.2em] uppercase">Signers</span>
            <span>5 / 5</span>
          </div>
          <div className="pt-2 text-[#90B0BF]">
            $&gt; integrity check: <span className="text-[#7C9CB1]">PASSED</span>
          </div>
        </div>
      </div>
    </section>

    {/* Digital Twin — metaverse-grade essence visualization */}
    <section
      id="digital-twin"
      data-testid="digital-twin-section"
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9]"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <SectionLabel testId="twin-label">Metaverse Digital Twin</SectionLabel>
          <h3 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-6">
            Your physical bottle,
            <br />
            rendered in real time.
          </h3>
          <p className="text-[#7C9CB1]/85 font-light leading-relaxed text-lg max-w-xl mb-4">
            Every Shreesavaa bottle carries an NTAG 22x DNA sensor. Each
            dispense and refill is signed on chain and mirrored here as a live
            essence level — the virtual twin of the vessel in your hand.
          </p>
          <p className="text-[#7C9CB1]/85 font-light leading-relaxed text-lg max-w-xl">
            Use the controls to simulate a guest pump or a cellar refill. The
            liquid responds, on chain, in the ritual.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs text-[#90B0BF]">
            <span>NTAG 22x DNA · tamper-evident</span>
            <span>Syncs to /twins/&lt;bottle&gt;</span>
            <span>Level update &lt; 500 ms</span>
          </div>
        </div>
        <div className="md:col-span-5">
          <BottleTwin bottleId="SH-042-0188" label="Ananta Radiance Serum" />
        </div>
      </div>
    </section>
  </div>
);

export default TransparencyPage;
